import * as React from "react";
import { useState } from "react";
import { findUserBy, labelForUserKey, type AppUser } from "../../services/userService";
import type { NoteRefArt } from "../../services/vorgangService";
import { kuerzelVon } from "../vorgang/shared/people";
import type { BezugKandidat } from "./bezugKandidaten";
import { KommentarEingabe, type KommentarSenden } from "./KommentarEingabe";
import { zerlegeKommentar, type KommentarZusatz } from "./kommentarInhalt";
import { KommentarText } from "./KommentarText";
import { type Kommentar } from "./kommentarModell";
import "./notizbloecke.css";
import "./kommentare.css";

/**
 * Der Kommentarfaden eines Elements (PO 13./14.09.2026) — die Liste der
 * Beiträge und darunter die Zeile zum Mitschreiben.
 *
 * ══ WIE IN WORD ════════════════════════════════════════════════════════════
 * Ein Element hat EINEN Faden, und der Faden ist flach: wer antwortet,
 * schreibt in denselben Faden. Es gibt keine Antwort auf die Antwort — in
 * einer Kanzleiakte ist die Frage „was wurde zu dieser Mail gesagt?" und nicht
 * „wer hat wem geantwortet?".
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ══ ÄNDERN UND LÖSCHEN GEHÖREN HIERHER ═════════════════════════════════════
 * Solange ein Kommentar ein gewöhnlicher Block im Strom war, konnte sein Autor
 * ihn korrigieren und durch Leerräumen löschen (`NotizFlaeche`:
 * `canEdit && e.autor === aktuellerUserId`). Seit er hinter der Sprechblase
 * liegt, steht er NICHT mehr im Strom (`versteckteKommentare`) — gäbe es die
 * beiden Wege hier nicht, bliebe ein Tippfehler oder ein versehentlich
 * abgeschickter Satz für immer in der Kundenakte stehen. In Word, dem Vorbild
 * des PO, lässt sich ein Kommentar löschen.
 *
 * Das RECHT bleibt dasselbe wie vorher am Block: nur der Autor, und nur mit
 * Schreibrecht am Vorgang. Das Backend prüft beides ein zweites Mal
 * (`TodoNoteService.update`: „Nur der Autor darf den Kommentar bearbeiten.").
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Erwähnungen, `#`-Bezüge und Anhänge: seit dem 28.09.2026 über
 * `KommentarEingabe` — mit denselben Auswahllisten wie im Notizfeld (PO:
 * „Erwähnen und # im Kommentar funktioniert nicht"). Frei getippte
 * `@kürzel` zählen weiter (`effectiveMentions`).
 */

const zeitpunkt = (d: Date): string => {
    if (!d || Number.isNaN(d.getTime()) || d.getTime() === 0) return "";
    const tag = d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
    const uhr = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
    const jahr = d.getFullYear() === new Date().getFullYear() ? "" : String(d.getFullYear());
    return `${tag}${jahr} ${uhr}`;
};

/**
 * Das Kürzel kommt seit dem 22.09.2026 aus `kuerzelVon` (PO: „bei den Notizen
 * bitte die Kürzel anzeigen, nicht die eigenen Abbreviations"). Hier stand
 * vorher eine eigene Ableitung aus dem Anzeigenamen.
 */

/** Einen eigenen Beitrag ändern — mit Bezügen und Anhängen (PO 28.09.2026). */
export type KommentarAendern = (
    k: Kommentar,
    text: string,
    mentions: string[],
    zusatz?: KommentarZusatz,
) => Promise<void>;

const HakenIcon: React.FC = () => (
    <svg
        width={11}
        height={11}
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.6}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
    >
        <path d="M4.5 10.5 8 14l7.5-8" />
    </svg>
);

/**
 * Das Schreibfeld beim ÄNDERN — mit dem zerlegten Bestand: `#Titel` statt
 * Chip, die Anhänge als Chips darunter. Ohne das verlöre ein Kommentar beim
 * Korrigieren eines Tippfehlers seine Anhänge.
 */
export const BeitragAendern: React.FC<{
    k: Kommentar;
    users: AppUser[];
    kandidaten?: BezugKandidat[];
    stil?: "spalte" | "faden";
    mitAnhang?: boolean;
    onSenden: KommentarSenden;
    onAbbrechen: () => void;
}> = ({ k, users, kandidaten, stil = "faden", mitAnhang = false, onSenden, onAbbrechen }) => {
    const [start] = useState(() => zerlegeKommentar(k));
    return (
        <KommentarEingabe
            users={users}
            {...(kandidaten ? { kandidaten } : {})}
            stil={stil}
            start={start}
            ariaLabel="Kommentar ändern"
            sendenLabel="Speichern"
            leerErlaubt
            mitAnhang={mitAnhang}
            autoFocus
            onSenden={onSenden}
            onAbbrechen={onAbbrechen}
        />
    );
};

const Beitrag: React.FC<{
    k: Kommentar;
    users: AppUser[];
    /** Darf DIESER Beitrag geändert werden? Nur der Autor, nur mit Schreibrecht. */
    eigener: boolean;
    onAendern?: KommentarAendern;
    onLoeschen?: (k: Kommentar) => Promise<void>;
    /** Der Haken (PO 28.09.2026: „Kommentare kann man erledigen"). */
    onErledigt?: (k: Kommentar, erledigt: boolean) => Promise<void>;
    kandidaten?: BezugKandidat[];
    onBezugOeffnen?: (art: NoteRefArt, id: string) => void;
    mitAnhang: boolean;
}> = ({ k, users, eigener, onAendern, onLoeschen, onErledigt, kandidaten, onBezugOeffnen, mitAnhang }) => {
    const name = labelForUserKey(k.autor, users) || k.autor;
    const [bearbeitet, setBearbeitet] = useState(false);
    const [busy, setBusy] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);
    /** Was der Haken zeigt, während der Server noch antwortet. */
    const [gewollt, setGewollt] = useState<boolean | null>(null);
    const erledigt = gewollt ?? !!k.erledigtAm;

    const darfAendern = eigener && !!onAendern;
    const darfLoeschen = eigener && !!onLoeschen;

    const oeffnen = () => {
        setFehler(null);
        setBearbeitet(true);
    };

    const abhaken = async () => {
        if (!onErledigt || busy) return;
        const ziel = !erledigt;
        setGewollt(ziel);
        setBusy(true);
        setFehler(null);
        try {
            await onErledigt(k, ziel);
        } catch (err: any) {
            console.error("[KommentarFaden] Haken setzen fehlgeschlagen", err);
            setGewollt(null);
            setFehler(err?.message ?? "Der Haken konnte nicht gesetzt werden.");
        } finally {
            setBusy(false);
        }
    };

    const loeschen = async () => {
        if (!onLoeschen || busy) return;
        setBusy(true);
        setFehler(null);
        try {
            await onLoeschen(k);
            // Kein `setBearbeitet(false)`: der Beitrag ist weg, die Liste
            // zeichnet neu — ein Zustand an einem entfernten Knoten liefe ins
            // Leere.
        } catch (err: any) {
            console.error("[KommentarFaden] Kommentar löschen fehlgeschlagen", err);
            setFehler(err?.message ?? "Kommentar konnte nicht gelöscht werden.");
            setBusy(false);
        }
    };

    return (
        <li className={`nb-kf-eintrag${erledigt ? " getan" : ""}`} data-kommentar={k.key}>
            <div className="nb-kf-kopf">
                <span className="nb-kf-av" aria-hidden>
                    {kuerzelVon(findUserBy(users, k.autor), name)}
                </span>
                <b className="nb-kf-name">{name}</b>
                <span className="nb-kf-zeit">{zeitpunkt(k.angelegt)}</span>
                {/* Die beiden Wege stehen am eigenen Beitrag IMMER da, nicht
                    erst beim Überfahren: der Faden ist klein, und ein Knopf,
                    den man suchen muss, wird nicht gefunden. */}
                {!bearbeitet && (darfAendern || darfLoeschen) ? (
                    <span className="nb-kf-tun">
                        {darfAendern ? (
                            <button
                                type="button"
                                className="nb-kf-tun-btn"
                                title="Kommentar ändern"
                                aria-label="Kommentar ändern"
                                disabled={busy}
                                onClick={oeffnen}
                            >
                                Ändern
                            </button>
                        ) : null}
                        {darfLoeschen ? (
                            <button
                                type="button"
                                className="nb-kf-tun-btn"
                                title="Kommentar löschen"
                                aria-label="Kommentar löschen"
                                disabled={busy}
                                onClick={() => void loeschen()}
                            >
                                Löschen
                            </button>
                        ) : null}
                    </span>
                ) : null}
                {/* DER HAKEN GEHÖRT JEDEM, DER DEN VORGANG SIEHT (PO 20.09.2026)
                    — auch am fremden Beitrag: gebeten wird ja der Erwähnte. */}
                {onErledigt ? (
                    <button
                        type="button"
                        className="nb-kf-hak"
                        aria-pressed={erledigt}
                        disabled={busy}
                        title={erledigt ? "Haken zurücknehmen" : "Als erledigt abhaken"}
                        aria-label={erledigt ? "Haken zurücknehmen" : "Als erledigt abhaken"}
                        onClick={() => void abhaken()}
                    >
                        <HakenIcon />
                    </button>
                ) : null}
            </div>
            {bearbeitet ? (
                <div className="nb-kf-edit">
                    <BeitragAendern
                        k={k}
                        users={users}
                        {...(kandidaten ? { kandidaten } : {})}
                        mitAnhang={mitAnhang}
                        onSenden={async (text, mentions, zusatz) => {
                            // Leergeräumt heißt gelöscht (PO 13.09.2026) —
                            // dieselbe Regel wie am Block.
                            if (!text.trim() && !zusatz) {
                                if (!onLoeschen) {
                                    throw new Error("Ein Kommentar ohne Text lässt sich nicht speichern.");
                                }
                                await loeschen();
                                return;
                            }
                            await onAendern!(k, text, mentions, zusatz);
                            setBearbeitet(false);
                        }}
                        onAbbrechen={() => setBearbeitet(false)}
                    />
                </div>
            ) : (
                <>
                    {/* Ein Kommentar kann Markup tragen (Chips, Anhänge) —
                        gereinigt mit demselben Reiniger wie am Block. */}
                    <KommentarText
                        k={k}
                        className={`nb-kf-text${erledigt ? " getan" : ""}`}
                        {...(onBezugOeffnen ? { onBezugOeffnen } : {})}
                    />
                    {fehler ? <div className="nb-kf-fehler">{fehler}</div> : null}
                </>
            )}
        </li>
    );
};

export const KommentarFaden: React.FC<{
    kommentare: Kommentar[];
    users: AppUser[];
    /** Worauf sich der Faden bezieht — steht über den Beiträgen. */
    titel: string;
    /** Fehlt er, ist der Faden nur zu lesen (kein Schreibrecht am Vorgang). */
    onNeu?: KommentarSenden;
    /**
     * Wer gerade liest. Entscheidet zusammen mit `onAendern`/`onLoeschen`, an
     * welchen Beiträgen die beiden Knöpfe stehen — nur am EIGENEN, wie vorher
     * am Block. Fehlt die Angabe, bleibt der Faden nur zu lesen.
     */
    aktuellerUserId?: string;
    /** Einen eigenen Beitrag korrigieren. */
    onAendern?: KommentarAendern;
    /** Einen eigenen Beitrag zurücknehmen. */
    onLoeschen?: (k: Kommentar) => Promise<void>;
    /**
     * Den Haken setzen (PO 28.09.2026: „Kommentare kann man erledigen, war im
     * Artifact auch so"). NICHT an das Schreibrecht gebunden: abhaken darf, wer
     * den Vorgang sieht (PO 20.09.2026). Fehlt er, steht kein Haken da.
     */
    onErledigt?: (k: Kommentar, erledigt: boolean) => Promise<void>;
    /** Das Angebot hinter `#` im Schreibfeld. */
    kandidaten?: BezugKandidat[];
    /** Klick auf einen Chip im Kommentar. */
    onBezugOeffnen?: (art: NoteRefArt, id: string) => void;
    /** Anhänge erlaubt — nur, wenn `onNeu`/`onAendern` den Zusatz auswerten. */
    mitAnhang?: boolean;
}> = ({
    kommentare,
    users,
    titel,
    onNeu,
    aktuellerUserId,
    onAendern,
    onLoeschen,
    onErledigt,
    kandidaten,
    onBezugOeffnen,
    mitAnhang = false,
}) => (
    <div className="nb-kf">
        <div className="nb-kf-titel">{titel}</div>

        {kommentare.length === 0 ? (
            <div className="nb-kf-leer">Noch kein Kommentar.</div>
        ) : (
            <ul className="nb-kf-liste">
                {kommentare.map((k) => (
                    <Beitrag
                        key={k.key}
                        k={k}
                        users={users}
                        eigener={!!aktuellerUserId && k.autor === aktuellerUserId}
                        {...(onAendern ? { onAendern } : {})}
                        {...(onLoeschen ? { onLoeschen } : {})}
                        {...(onErledigt ? { onErledigt } : {})}
                        {...(kandidaten ? { kandidaten } : {})}
                        {...(onBezugOeffnen ? { onBezugOeffnen } : {})}
                        mitAnhang={mitAnhang}
                    />
                ))}
            </ul>
        )}

        {onNeu ? (
            <div className="nb-kf-neu">
                {/* Der Faden bleibt nach dem Absenden offen — man kommentiert
                    selten nur einmal. */}
                <KommentarEingabe
                    users={users}
                    {...(kandidaten ? { kandidaten } : {})}
                    stil="faden"
                    ariaLabel="Kommentar schreiben"
                    mitAnhang={mitAnhang}
                    onSenden={onNeu}
                />
            </div>
        ) : null}
    </div>
);
